import React, { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Breadcrumb, Col, Row, Space, Spin } from 'antd'
import { isEmpty, isNil } from 'lodash'
import urlJoin from 'url-join'

import { UpdateDataText } from '../../../core/components/sections'
import { showErrorNotification } from '../../../core/helpers'

import { StyledPageSubTitle } from '../../../common/components/styled'
import { APP_ROUTES } from '../../../common/constants'
import { useDocumentTitle } from '../../../common/hooks'

import {
  useCreateUserMutation,
  useRolesQuery,
  useUpdateUserMutation,
  useUserDetailsQuery,
} from '../../hooks'
import { UserEditorForm } from '../forms'

const UserEditorPage = () => {
  const { id: userId } = useParams()
  const documentTitle = userId ? 'Edit user' : 'Create user'
  useDocumentTitle(documentTitle)
  const navigate = useNavigate()

  const { languageOptions, cuisineTypeOptions } = useSelector(
    store => store.common.metadata.data,
  )

  const [
    fetchUserDetails,
    { data: user, isLoading: isLoadingUser, error: userError },
  ] = useUserDetailsQuery(true)

  const [createUser] = useCreateUserMutation()
  const [updateUser] = useUpdateUserMutation()

  const {
    options: roleOptions,
    isLoading: isLoadingRoles,
    error: roleError,
  } = useRolesQuery()

  const handleSubmit = useCallback(
    userData => {
      if (userId) {
        updateUser(userId, userData)
      } else {
        createUser(userData)
      }
    },
    [createUser, updateUser, userId],
  )

  useEffect(() => {
    if (isNil(userId) && !isNil(user?.id)) {
      navigate(urlJoin(APP_ROUTES.MISC.children.USERS.fullPath, user?.id))
    }
  }, [navigate, user?.id, userId])

  useEffect(() => {
    if (!isNil(userId)) {
      fetchUserDetails(userId)
    }
  }, [fetchUserDetails, userId])

  useEffect(() => {
    if (userError || roleError) {
      showErrorNotification({
        title: 'User error',
        description: userError || roleError,
      })
    }
  }, [roleError, userError])

  if (
    (!isNil(userId) && isEmpty(user)) ||
    isLoadingUser ||
    isEmpty(roleOptions) ||
    isLoadingRoles
  ) {
    return <Spin />
  }

  return (
    <>
      <StyledPageSubTitle>
        <Row justify="space-between">
          <Col span={12}>
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to={APP_ROUTES.MISC.children.USERS.fullPath}>
                  {APP_ROUTES.MISC.children.USERS.label}
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{user?.name}</Breadcrumb.Item>
            </Breadcrumb>
          </Col>

          {user && (
            <Space>
              <UpdateDataText
                updatedAt={user.updatedAt}
                updatedBy={user.updatedBy?.name}
              />
            </Space>
          )}
        </Row>
      </StyledPageSubTitle>
      <UserEditorForm
        user={user}
        roleOptions={roleOptions}
        cuisineTypeOptions={cuisineTypeOptions}
        languageOptions={languageOptions}
        onSubmit={handleSubmit}
      />
    </>
  )
}

export default UserEditorPage
